/**
 * Pure mutation functions for preview actions.
 */

import type {DocumentPreview, DocumentPreviewDiff, IDocumentData} from "@buildwithflux/models";
import {applyChange, revertChange} from "deep-diff";

/**
 * Pure mutation function for renderPreview.
 * Reverts any existing preview, locks preview nodes, applies new diff, and stores metadata.
 * Requires the original (non-draft) document for deep-diff revertChange/applyChange.
 */
export function applyRenderPreview(draft: IDocumentData, document: IDocumentData, preview: DocumentPreview): void {
    if (draft.preview) {
        for (const nodeUid in draft.preview.initialLockValueMap) {
            const node = draft.pcbLayoutNodes[nodeUid];
            if (node) {
                node.locked = draft.preview.initialLockValueMap[nodeUid];
            }
        }
        for (const change of draft.preview.diff) {
            revertChange(draft, document, change);
        }
    }

    const initialLockValueMap: Record<string, boolean | undefined> = {};
    for (const previewNodeUid of preview.previewNodeUids) {
        const node = draft.pcbLayoutNodes[previewNodeUid];
        if (node) {
            initialLockValueMap[previewNodeUid] = node.locked;
            node.locked = true;
        }
    }

    for (const change of preview.diff) {
        applyChange(draft, document, change);
    }

    draft.preview = {
        ...preview,
        initialLockValueMap,
    };
}

/**
 * Pure mutation function for clearPreview.
 * Restores original lock values, reverts all preview changes, and removes metadata.
 * Requires the original (non-draft) document for deep-diff revertChange.
 */
export function applyClearPreview(draft: IDocumentData, document: IDocumentData): void {
    if (!draft.preview) {
        return;
    }

    for (const nodeUid in draft.preview.initialLockValueMap) {
        const node = draft.pcbLayoutNodes[nodeUid];
        if (node) {
            node.locked = draft.preview.initialLockValueMap[nodeUid];
        }
    }

    for (const change of draft.preview.diff) {
        revertChange(draft, document, change);
    }

    delete draft.preview;
}

/**
 * Pure mutation function for applyPreview.
 * Applies preview diff changes to the document permanently.
 * Requires the original (non-draft) document for deep-diff applyChange.
 */
export function applyApplyPreview(
    draft: IDocumentData,
    document: IDocumentData,
    previewDiff: DocumentPreviewDiff,
): void {
    for (const change of previewDiff) {
        applyChange(draft, document, change);
    }
}
